/**
 * Local data service for development
 * Provides persistence for projects and files until a real backend is connected
 */

// Local storage keys
import * as XLSX from 'xlsx';

const STORAGE_KEYS = {
  PROJECTS: 'spreadsheet-insights-projects',
  PROJECT_SETTINGS: 'spreadsheet-insights-project-settings'
};

// Initial data if nothing exists in storage
const initialData = {
  projects: []
};

// Helper to safely parse JSON from localStorage
const safelyParseJSON = (key, defaultValue) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : defaultValue;
  } catch (e) {
    console.error(`Error parsing localStorage data for key ${key}:`, e);
    return defaultValue;
  }
};

// Helper to safely stringify and save JSON to localStorage
const safelySaveJSON = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (e) {
    console.error(`Error saving to localStorage for key ${key}:`, e);
    return false;
  }
};

// Local data service
export const saveProjectSettings = (projectId, settings) => {
  const allSettings = safelyParseJSON(STORAGE_KEYS.PROJECT_SETTINGS, {});
  allSettings[projectId] = settings;
  return safelySaveJSON(STORAGE_KEYS.PROJECT_SETTINGS, allSettings);
};

export const loadProjectSettings = (projectId) => {
  const allSettings = safelyParseJSON(STORAGE_KEYS.PROJECT_SETTINGS, {});
  return allSettings[projectId] || null;
};

export const localData = {
  // Clear all data from localStorage
  clearAllData: () => {
    console.log('Clearing all local data');
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
      console.log(`Cleared ${key} from localStorage`);
    });
    console.log('All data cleared successfully');
    return true;
  },

  // Get all projects
  getProjects: () => {
    return safelyParseJSON(STORAGE_KEYS.PROJECTS, initialData.projects);
  },
  
  // Get a single project by ID
  getProjectById: (id) => {
    const projects = localData.getProjects();
    return projects.find(project => project.id === id);
  },

  // Get a single project by name
  getProjectByName: (name) => {
    const projects = localData.getProjects();
    return projects.find(project => project.name === name);
  },
  
  // Create a new project (async, processes files like addFilesToProject)
  createProject: async (projectData) => {
    const projects = localData.getProjects();
    const projectId = projectData.id || `project-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    
    // Process files (if any) as in addFilesToProject
    let processedFiles = [];
    if (Array.isArray(projectData.files) && projectData.files.length > 0) {
      // Use the same logic as addFilesToProject
      for (let i = 0; i < projectData.files.length; i++) {
        const file = projectData.files[i];
        const fileId = `file-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        const newFileObj = {
          id: fileId,
          name: file.name,
          type: file.type || 'spreadsheet',
          lastModified: file.lastModified,
          uploadDate: new Date().toISOString(),
          size: file.size || 0,
          rawContent: null,
          contentType: file.type,
          contentSize: 0,
          isParsed: false,
          readError: null
        };
        if (file instanceof File || (typeof file === 'object' && file.name && (file.arrayBuffer || file.slice))) {
          try {
            const fileContent = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = event => resolve(event.target.result);
              reader.onerror = error => reject(error);
              reader.readAsArrayBuffer(file);
            });
            // Convert to base64
            const binary = new Uint8Array(fileContent);
            let base64 = '';
            for (let j = 0; j < binary.length; j++) {
              base64 += String.fromCharCode(binary[j]);
            }
            newFileObj.rawContent = btoa(base64);
            newFileObj.contentSize = base64.length;
          } catch (error) {
            newFileObj.readError = error.message || 'Failed to read file';
          }
        } else if (file.rawContent) {
          // Already has base64 content (from Electron bridge)
          newFileObj.rawContent = file.rawContent;
          newFileObj.contentSize = file.rawContent.length;
        }
        processedFiles.push(newFileObj);
      }
    }
    
    const newProject = {
      id: projectId,
      name: projectData.name || 'Untitled Project',
      description: projectData.description || '',
      client: projectData.client || '',
      tags: projectData.tags || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      files: processedFiles,
      fileCount: processedFiles.length
    };
    
    projects.push(newProject);
    safelySaveJSON(STORAGE_KEYS.PROJECTS, projects);
    
    return newProject;
  },
  
  // Add files to a project
  addFilesToProject: async (projectId, files) => {
    const projects = localData.getProjects();
    const projectIndex = projects.findIndex(project => project.id === projectId);
    
    if (projectIndex === -1) {
      throw new Error(`Project with ID ${projectId} not found`);
    }
    
    console.log(`Adding ${files.length} files to project ${projectId}`);
    console.log('Files to add:', files);
    
    // Process files to ensure they have required fields
    const processedFiles = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileId = `file-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      
      console.log(`Processing file ${i+1}/${files.length}: ${file.name}, assigned ID: ${fileId}`);
      
      const newFileObj = {
        id: fileId,
        name: file.name,
        type: file.type || 'spreadsheet',
        lastModified: file.lastModified,
        uploadDate: new Date().toISOString(),
        size: file.size || 0,
        rawContent: null,
        contentType: file.type,
        contentSize: 0,
        isParsed: false,
        readError: null
      };

      // Check if it's a File object from browser upload
      if (file instanceof File || 
          (typeof file === 'object' && file.name && (file.arrayBuffer || file.slice))) {
        // This is a File object, we need to read its content
        try {
          console.log(`Reading content from File object for: ${file.name}`);
          
          // Use FileReader to read the file content
          const fileContent = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = event => resolve(event.target.result);
            reader.onerror = error => reject(error);
            reader.readAsArrayBuffer(file);
          });
          
          // Convert ArrayBuffer to base64 string for storage
          const array = new Uint8Array(fileContent);
          let binary = '';
          for (let j = 0; j < array.length; j++) {
            binary += String.fromCharCode(array[j]);
          }
          
          newFileObj.rawContent = btoa(binary);
          newFileObj.contentSize = binary.length;
          
          console.log(`Successfully read content for ${file.name}:`);
          console.log(`- Content size: ${newFileObj.contentSize} bytes`);
          console.log(`- First 100 chars of base64: ${newFileObj.rawContent.substring(0, 100)}...`);
          
        } catch (readError) {
          console.error(`Error reading file ${file.name}:`, readError);
          newFileObj.readError = `Failed to read file: ${readError.message}`;
        }
      } else {
        console.warn(`File ${file.name} is not a proper File object, cannot read content`);
        newFileObj.readError = 'Not a valid File object';
      }
      
      processedFiles.push(newFileObj);
    }

    console.log(`Processed ${processedFiles.length} files for addition to project`);
    
    // Add the new files to the project
    projects[projectIndex].files = [
      ...projects[projectIndex].files,
      ...processedFiles
    ];

    projects[projectIndex].updatedAt = new Date().toISOString();
    projects[projectIndex].fileCount = projects[projectIndex].files.length;

    console.log(`Saving updated project with ${projects[projectIndex].files.length} total files`);
    
    // Log file content presence
    const filesWithContent = projects[projectIndex].files.filter(f => !!f.rawContent).length;
    console.log(`Files with content: ${filesWithContent}/${projects[projectIndex].files.length}`);
    
    safelySaveJSON(STORAGE_KEYS.PROJECTS, projects);
    return projects[projectIndex];
  },

  // Update a project
  updateProject: (projectId, updateData) => {
    const projects = localData.getProjects();
    const projectIndex = projects.findIndex(project => project.id === projectId);

    if (projectIndex === -1) {
      throw new Error(`Project with ID ${projectId} not found`);
    }

    projects[projectIndex] = {
      ...projects[projectIndex],
      ...updateData,
      updatedAt: new Date().toISOString()
    };

    safelySaveJSON(STORAGE_KEYS.PROJECTS, projects);
    return projects[projectIndex];
  },

  // Delete a file from a project
  deleteFileFromProject: (projectId, fileId) => {
    const projects = localData.getProjects();
    const projectIndex = projects.findIndex(project => project.id === projectId);
    if (projectIndex === -1) throw new Error(`Project with ID ${projectId} not found`);
    projects[projectIndex].files = projects[projectIndex].files.filter(f => f.id !== fileId && f.name !== fileId);
    projects[projectIndex].updatedAt = new Date().toISOString();
    safelySaveJSON(STORAGE_KEYS.PROJECTS, projects);
    return true;
  },

  // Delete a project
  deleteProject: (projectId) => {
    const projects = localData.getProjects();
    const filteredProjects = projects.filter(project => project.id !== projectId);
    
    safelySaveJSON(STORAGE_KEYS.PROJECTS, filteredProjects);
    return true;
  },

  /**
   * Parse CSV text into a 2D array of strings
   * Handles quoted values, escaped quotes, and ensures consistent column count
   */
  parseCSV: (csvText) => {
    const rows = [];
    let currentRow = [];
    let currentCell = '';
    let inQuotes = false;
    
    for (let i = 0; i < csvText.length; i++) {
      const char = csvText[i];
      const nextChar = csvText[i + 1] || '';
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote inside quoted field
          currentCell += '"';
          i++; // Skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // End of cell
        currentRow.push(currentCell);
        currentCell = '';
      } else if ((char === '\n' || char === '\r') && !inQuotes) {
        // End of row
        currentRow.push(currentCell);
        if (currentRow.some(cell => cell.trim() !== '')) {
          rows.push([...currentRow]);
        }
        currentRow = [];
        currentCell = '';
        // Skip next newline if it's part of CRLF
        if (char === '\r' && nextChar === '\n') i++;
      } else {
        currentCell += char;
      }
    }
    
    // Add the last cell and row if they exist
    if (currentCell.trim() !== '' || currentRow.length > 0) {
      currentRow.push(currentCell);
      if (currentRow.some(cell => cell.trim() !== '')) {
        rows.push([...currentRow]);
      }
    }
    
    // Ensure all rows have the same number of columns as the longest row
    const maxCols = rows.length > 0 ? Math.max(...rows.map(row => row.length)) : 0;
    return rows.map(row => {
      const newRow = [...row];
      while (newRow.length < maxCols) newRow.push('');
      return newRow.map(cell => cell.trim());
    });
  },

  // Get file content from storage and parse it properly
  getFileContent: (projectId, fileId) => {
    console.log(`------------------ START FILE CONTENT LOADING ------------------`);
    console.log(`getFileContent called for project: ${projectId}, file: ${fileId}`);
    
    // Get project and file
    const project = localData.getProjectById(projectId);
    if (!project) {
      console.error(`Project not found: ${projectId}`);
      return null;
    }
    
    let file = project.files && project.files.find(f => f.id === fileId);
    // Fallback: try matching by file name if not found by ID
    if (!file && project.files) {
      file = project.files.find(f => f.name === fileId);
    }
    if (!file) {
      console.error(`File not found: ${fileId} in project ${projectId}`);
      return null;
    }
    
    console.log(`Processing file: ${file.name}, type: ${file.type || 'unknown'}, size: ${file.size || 'unknown'} bytes`);
    
    if (!file.rawContent) {
      console.error(`No raw content found for file: ${file.name}`);
      return {
        fileName: file.name,
        fileId: file.id,
        sheets: [{
          name: 'Sheet1',
          data: [
            ['Error'],
            ['No content available for this file']
          ],
          rowCount: 2,
          columnCount: 1
        }]
      };
    }
    
    try {
      // Convert base64 to binary
      const binary = atob(file.rawContent);
      console.log(`Decoded base64 content, binary length: ${binary.length}`);
      
      // Handle Excel files
      if (file.name.match(/\.(xlsx?|xlsm)$/i)) {
        console.log('Processing as Excel file');
        const workbook = XLSX.read(binary, { type: 'binary' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
        
        console.log(`Parsed Excel file: ${jsonData.length} rows`);
        if (jsonData.length > 0) {
          console.log(`First row has ${jsonData[0].length} columns`);
        }
        
        return {
          fileName: file.name,
          fileId: file.id,
          sheets: [{
            name: firstSheetName,
            data: jsonData,
            rowCount: jsonData.length,
            columnCount: jsonData.length > 0 ? jsonData[0].length : 0
          }]
        };
      }
      
      // Handle CSV files
      console.log('Processing as CSV file');
      let text = '';
      
      try {
        // Try UTF-8 first, fall back to binary if that fails
        text = new TextDecoder('utf-8').decode(new Uint8Array([...binary].map(c => c.charCodeAt(0))));
      } catch (e) {
        console.warn('UTF-8 decode failed, using binary directly');
        text = binary;
      }
      
      // Normalize line endings and remove BOM
      text = text.replace(/\r\n?/g, '\n').replace(/^\uFEFF/, '');
      
      // Parse CSV
      console.log('Parsing CSV content');
      const parsedData = localData.parseCSV(text);
      
      // Log parsing results
      if (parsedData.length > 0) {
        console.log(`Parsed ${parsedData.length} rows, ${parsedData[0].length} columns`);
        console.log('Sample data:', parsedData.slice(0, Math.min(3, parsedData.length)));
      } else {
        console.warn('No data parsed from CSV');
      }
      
      return {
        fileName: file.name,
        fileId: file.id,
        sheets: [{
          name: 'Sheet1',
          data: parsedData,
          rowCount: parsedData.length,
          columnCount: parsedData.length > 0 ? parsedData[0].length : 0
        }]
      };
      
    } catch (error) {
      console.error('Error processing file:', error);
      return {
        fileName: file.name,
        fileId: file.id,
        sheets: [{
          name: 'Sheet1',
          data: [
            ['Error'],
            ['Failed to process file: ' + (error.message || 'Unknown error')]
          ],
          rowCount: 2,
          columnCount: 1
        }]
      };
    }
  },

  // Utility methods
  // No additional utility methods needed

  initWithSampleData: () => {
    // Return false to indicate no sample data was initialized
    return false;
  },
};

export default localData;
